// Training Center → Dashboard tab.
// Mirrors web/scripts/training-center.js's tcLoadDashboard() + the
// _tcRender* chart helpers. Pulls /admin/overview for the KPI block
// + Latency + Volume + Feedback trends, then /admin/quality/trend for
// the multi-line quality chart.

import type { EChartsOption } from "echarts";
import { useEffect, useState } from "react";
import { adminApi, type OverviewResponse } from "../../api/admin";
import { trainingApi, type OrgScope } from "../../api/training";
import {
  Chart,
  ErrorBlock,
  KpiCard,
  LoadingBlock,
  formatNumber,
  formatPercent,
  formatRelative,
  lineTrendOption,
} from "../admin/_shared";

interface Props {
  bearer: string;
  scope: OrgScope;
}

interface FeedbackSummary {
  good?: number;
  bad?: number;
  positive_rate_30d?: number;
}

interface ExtendedOverview extends OverviewResponse {
  feedback_kpis?: { positive_rate_30d?: number };
  feedback_summary_30d?: FeedbackSummary;
  latency_by_day?: Array<{ day: string; avg_ms?: number; p95_ms?: number }>;
  golden_eval?: { latest_pass_rate?: number; latest_completed_at?: string };
  pending_recommendations?: number;
  quality_kpis?: {
    avg_overall_30d?: number;
    avg_relevance_30d?: number;
    avg_sql_correctness_30d?: number;
    scored_count_30d?: number;
  };
}

export function DashboardTab({ bearer, scope }: Props) {
  const [overview, setOverview] = useState<ExtendedOverview | null>(null);
  const [qualityTrend, setQualityTrend] = useState<
    Array<{ day: string; overall?: number; relevance?: number; sql?: number; entity?: number; output?: number }>
  >([]);
  const [error, setError] = useState<Error | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    Promise.all([
      adminApi.overview(bearer, scope) as Promise<ExtendedOverview>,
      trainingApi.qualityTrend(bearer, scope).catch(() => []),
    ])
      .then(([o, t]) => {
        if (cancelled) return;
        setOverview(o);
        setQualityTrend(t);
        setLoading(false);
      })
      .catch((e: Error) => {
        if (cancelled) return;
        setError(e);
        setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [bearer, scope]);

  if (error) return <ErrorBlock error={error} />;
  if (loading || !overview) return <LoadingBlock />;

  const qk = overview.quality_kpis || {};
  const fb = overview.feedback_kpis || {};
  const ge = overview.golden_eval || {};

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-7 gap-3">
        <KpiCard
          label="Quality Score"
          value={qk.avg_overall_30d != null ? `${Math.round(qk.avg_overall_30d * 100)}%` : "—"}
        />
        <KpiCard label="Avg Relevance" value={formatPercent(qk.avg_relevance_30d, 0)} />
        <KpiCard label="Avg SQL Correctness" value={formatPercent(qk.avg_sql_correctness_30d, 0)} />
        <KpiCard label="Scored Queries (30d)" value={formatNumber(qk.scored_count_30d)} />
        <KpiCard label="Feedback Positive Rate" value={formatPercent(fb.positive_rate_30d, 1)} />
        <KpiCard
          label="Pending Recommendations"
          value={formatNumber(overview.pending_recommendations)}
        />
        <KpiCard label="Golden Eval Pass Rate">
          <div className="flex flex-col">
            <div className="text-2xl font-semibold text-neutral-900">
              {ge.latest_pass_rate != null ? `${Math.round(ge.latest_pass_rate * 100)}%` : "—"}
            </div>
            {ge.latest_completed_at && (
              <div className="text-[11px] text-neutral-500 mt-0.5">
                Last run: {formatRelative(ge.latest_completed_at)}
              </div>
            )}
          </div>
        </KpiCard>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <ChartCard title="Quality Scores (last 30 days)" empty={qualityTrend.length === 0}>
          <Chart option={qualityMultiLineOption(qualityTrend)} height={260} />
        </ChartCard>

        <ChartCard
          title="Latency (last 30 days)"
          empty={!overview.latency_by_day || overview.latency_by_day.length === 0}
        >
          <Chart option={latencyOption(overview.latency_by_day || [])} height={260} />
        </ChartCard>

        <ChartCard
          title="Query Volume (last 30 days)"
          empty={!overview.query_volume_by_day || overview.query_volume_by_day.length === 0}
        >
          <Chart option={lineTrendOption(overview.query_volume_by_day || [])} height={260} />
        </ChartCard>

        <ChartCard
          title="User Feedback (last 30 days)"
          empty={feedbackEmpty(overview.feedback_summary_30d)}
        >
          <Chart option={feedbackOption(overview.feedback_summary_30d || {})} height={260} />
        </ChartCard>
      </div>
    </div>
  );
}

function ChartCard({
  title,
  empty,
  children,
}: {
  title: string;
  empty: boolean;
  children: React.ReactNode;
}) {
  return (
    <div className="rounded-[14px] border border-line bg-white p-4">
      <div className="text-sm font-medium text-neutral-900 mb-2">{title}</div>
      {empty ? (
        <div className="text-sm text-neutral-500 py-12 text-center">No data yet</div>
      ) : (
        children
      )}
    </div>
  );
}

function feedbackEmpty(s?: FeedbackSummary): boolean {
  if (!s) return true;
  return !s.good && !s.bad;
}

function qualityMultiLineOption(
  points: Array<{
    day: string;
    overall?: number;
    relevance?: number;
    sql?: number;
    entity?: number;
    output?: number;
  }>,
): EChartsOption {
  const series = [
    { key: "overall", label: "Overall", color: "#0d6efd" },
    { key: "relevance", label: "Relevance", color: "#198754" },
    { key: "sql", label: "SQL", color: "#dc3545" },
    { key: "entity", label: "Entity", color: "#ffc107" },
    { key: "output", label: "Output", color: "#6f42c1" },
  ] as const;
  return {
    grid: { left: 36, right: 12, top: 24, bottom: 36 },
    legend: { type: "scroll", bottom: 0, textStyle: { fontSize: 11 } },
    tooltip: { trigger: "axis" },
    xAxis: {
      type: "category",
      data: points.map((p) => p.day),
      axisLabel: { fontSize: 11, hideOverlap: true },
      axisLine: { lineStyle: { color: "#e5e7eb" } },
      axisTick: { show: false },
    },
    yAxis: {
      type: "value",
      min: 0,
      max: 1,
      axisLabel: {
        fontSize: 11,
        formatter: (v: number) => `${Math.round(v * 100)}%`,
      },
      axisLine: { show: false },
      axisTick: { show: false },
      splitLine: { lineStyle: { color: "#f3f4f6" } },
    },
    series: series.map((s) => ({
      type: "line",
      name: s.label,
      data: points.map((p) => p[s.key] ?? null),
      showSymbol: false,
      smooth: true,
      lineStyle: { color: s.color, width: s.key === "overall" ? 2 : 1.5 },
      itemStyle: { color: s.color },
    })),
  };
}

function latencyOption(
  points: Array<{ day: string; avg_ms?: number; p95_ms?: number }>,
): EChartsOption {
  return {
    grid: { left: 40, right: 12, top: 24, bottom: 30 },
    legend: { bottom: 0, textStyle: { fontSize: 11 } },
    tooltip: { trigger: "axis" },
    xAxis: {
      type: "category",
      data: points.map((p) => p.day),
      axisLabel: { fontSize: 11, hideOverlap: true },
      axisLine: { lineStyle: { color: "#e5e7eb" } },
      axisTick: { show: false },
    },
    yAxis: {
      type: "value",
      axisLabel: { fontSize: 11, formatter: (v: number) => `${v} ms` },
      axisLine: { show: false },
      axisTick: { show: false },
      splitLine: { lineStyle: { color: "#f3f4f6" } },
    },
    series: [
      {
        type: "line",
        name: "Avg",
        data: points.map((p) => p.avg_ms ?? null),
        showSymbol: false,
        smooth: true,
        areaStyle: { color: "rgba(13, 110, 253, 0.08)" },
        lineStyle: { color: "#0d6efd", width: 2 },
      },
      {
        type: "line",
        name: "P95",
        data: points.map((p) => p.p95_ms ?? null),
        showSymbol: false,
        smooth: true,
        lineStyle: { color: "#dc3545", width: 1.5 },
      },
    ],
  };
}

function feedbackOption(s: FeedbackSummary): EChartsOption {
  const good = s.good ?? 0;
  const bad = s.bad ?? 0;
  return {
    grid: { left: 36, right: 12, top: 24, bottom: 30 },
    tooltip: { trigger: "axis", axisPointer: { type: "shadow" } },
    xAxis: {
      type: "category",
      data: ["Good", "Bad"],
      axisLine: { lineStyle: { color: "#e5e7eb" } },
      axisTick: { show: false },
      axisLabel: { fontSize: 12 },
    },
    yAxis: {
      type: "value",
      axisLine: { show: false },
      axisTick: { show: false },
      splitLine: { lineStyle: { color: "#f3f4f6" } },
      axisLabel: { fontSize: 11 },
    },
    series: [
      {
        type: "bar",
        data: [
          { value: good, itemStyle: { color: "#16a34a" } },
          { value: bad, itemStyle: { color: "#dc2626" } },
        ],
        barMaxWidth: 80,
        itemStyle: { borderRadius: [4, 4, 0, 0] },
      },
    ],
  };
}
